import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import SVG from 'react-inlinesvg';
import ChevronRightIcon from '../../../../../../../../assets/images/reskin-images/icon--chevrons-right.svg';
import { adminAPI } from '../../../../../api';
import FormLoader from '../../../../../components/FormLoader';
import useAlarm from '../../../../../context/AlarmContext';
import FormInput from '../../../../../components/FormInput';
import SelectInput from '../../../../../components/SelectInput';
import SearchableSelectInput from '../../../../../components/SearchableSelectInput';

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

const CohortForm = ({ id, type, setOpenSideActionBoard, onSuccess }) => {
  const { setAlarm } = useAlarm();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    alex_about_cohort: '',
    user_access_type: '',
    alex_access_type: '',
    company_name: '',
    company_id: '',
    start_date: '',
    user_expiration_date: '',
    alex_expiration_date: '',
    status: 'Pending',
    splash_screen: '',
    cohort_pricing: '',
    cra_contact: '',
    account_manager: '',
    primary_client_contact_name: '',
    primary_client_contact_email: '',
    total_seats: '',
    used_seats: '',
    available_seats: '',
    cohort_notes: '',
    reporting_cadence: '',
    representativeEmails: [],
    userEmails: [],
    cohort_courses: [],
  });

  const { data: cohort, refetch } = useQuery(
    ['getCohortDetails', { id: id || '' }],
    adminAPI.getCohortDetails,
    {
      enabled: type === 'edit' || type === 'view',
    }
  );

  const { data: invitationData } = useQuery(
    'getInvitationData',
    adminAPI.getInvitationData
  );

  const courses = invitationData?.enabled_courses || [];
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);
  
  useEffect(() => {
    if (type === 'edit' || type === 'view') {
      refetch();
    }
  }, [id, refetch, type]);

  useEffect(() => {
    if (cohort) {
      setFormData({
        name: cohort.name || '',
        representativeEmails: (cohort.representativeEmails || []).map(rep => rep.email),
        userEmails: (cohort.userEmails || []).map(user => user.email),
        alex_about_cohort: cohort.alex_about_cohort || '',
        user_access_type: cohort.user_access_type || '',
        alex_access_type: cohort.alex_access_type || '',
        company_name: cohort.company_name || '',
        company_id: cohort.company_id || '',
        start_date: formatDateForInput(cohort.start_date),
        user_expiration_date: formatDateForInput(cohort.user_expiration_date),
        alex_expiration_date: formatDateForInput(cohort.alex_expiration_date),
        status: cohort.status || '',
        splash_screen: cohort.splash_screen || '',
        cohort_pricing: cohort.cohort_pricing || '',
        cra_contact: cohort.cra_contact || '',
        account_manager: cohort.account_manager || '',
        primary_client_contact_name: cohort.primary_client_contact_name || '',
        primary_client_contact_email: cohort.primary_client_contact_email || '',
        total_seats: cohort.total_seats || '',
        used_seats: cohort.used_seats || '',
        available_seats: cohort.available_seats || '',
        cohort_notes: cohort.cohort_notes || '',
        reporting_cadence: cohort.reporting_cadence || '',
        cohort_courses: selectedCourses || [],
      });
    }
  }, [cohort]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (cohort?.cohort_courses && cohort.cohort_courses.length > 0) {
      const initiallyEnrolledCourses = cohort.cohort_courses
        .map((course) => course?.id);
      setSelectedCourses(initiallyEnrolledCourses);
    }
  }, [cohort?.cohort_courses]);

  const handleCheckAll = (checked) => {
    if (checked) {
      setSelectedCourses(invitationData?.enabled_courses.map((course) => course?.id));
      setIsCheckAll(true);
    } else {
      setSelectedCourses([]);
      setIsCheckAll(false);
    }
  };

  const handleCourseCheck = (checked, courseId) => {
    if (checked) {
      setSelectedCourses((prevCourses) => [...prevCourses, courseId]);
    } else {
      setSelectedCourses((prevCourses) =>
        prevCourses.filter((id) => id !== courseId)
      );
    }
  };

  const { mutate: createCohortMutation, isLoading: createLoading } =
    useMutation(adminAPI.createCohort, {
      onSuccess: () => {
        setOpenSideActionBoard();
        setAlarm({ type: 'success', message: 'Cohort created successfully' });
        queryClient.invalidateQueries('getAllCohorts');
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
        }
      },
      onError: (error) => {
        const errors = error?.response?.data?.errors;
        const errorMessage = errors?.length > 0
          ? errors.join(', ')
          : error.message;
        setAlarm({ type: 'error', message: errorMessage });
      },
    });

  const { mutate: updateCohortMutation, isLoading: updateLoading } =
    useMutation(adminAPI.updateCohort, {
      onSuccess: () => {
        refetch();
        setOpenSideActionBoard();
        setAlarm({ type: 'success', message: 'Cohort updated successfully' });
        queryClient.invalidateQueries('getAllCohorts');
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
        }
      },
      onError: (error) => {
        const errors = error?.response?.data?.errors;
        const errorMessage = errors?.length > 0
          ? errors.join(', ')
          : error.message;
        setAlarm({ type: 'error', message: errorMessage });
      },
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    const cohortData = {
      ...formData,
      cohort_courses: selectedCourses.map((courseId) => ({ id: courseId })),
      id,
    }
    if (type === 'create') {
      createCohortMutation({cohort: cohortData});
    } else if (type === 'edit') {
      updateCohortMutation({id, cohort: cohortData});
    }
  };

  const isDetailView = type === 'view';

  const label = {
    view: 'View Cohort',
    edit: 'Edit Cohort',
    create: 'Create Cohort',
  };

  const hubspotAccessTypes = [
    { value: '', label: '' },
    ...invitationData?.hubspot_options || []
  ];

  const alexAccessTypes = [
    { value: '', label: '' },
    ...invitationData?.alex_access_type_options || []
  ];

  const statusOptions = [
    { value: '', label: '' },
    { value: 'Active', label: 'Active' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Unconfirmed', label: 'Unconfirmed' },
    { value: 'Upcoming', label: 'Upcoming' },
  ];

  const splashScreenOptions = invitationData?.splash_screen_options || [{ value: '', label: 'None' }];

  return (
    <>
      <div className="flex justify-between items-center relative pb-4 px-8">
        <button
          className="p-2 flex items-center -ml-2"
          type="button"
          onClick={() => setOpenSideActionBoard()}
        >
          <SVG src={ChevronRightIcon} />
        </button>
        <span
          style={{ width: 'calc(100% + 6rem)' }}
          className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
        />
      </div>

      <form
        className="overflow-y-scroll h-full px-8 py-8"
        onSubmit={handleSubmit}
      >
        <h1>{label[type]}</h1>

        <div style={{ gap: '16px' }} className="flex flex-col my-12">
          <div className="mb-4">
            <label className="text-sm font-semibold text-gray-700">NAME</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              disabled={isDetailView}
              required
            />
          </div>

          <div className="mb-4">
            <label className="font-semibold text-gray-700">About Cohort</label>
            <textarea
              name="alex_about_cohort"
              value={formData.alex_about_cohort}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              rows="4"
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">User Access Type</p>
            <SelectInput
              name="user_access_type"
              value={formData.user_access_type}
              options={hubspotAccessTypes}
              onChange={handleFieldChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">Alex Access Type</p>
            <SelectInput
              name="alex_access_type"
              value={formData.alex_access_type}
              options={alexAccessTypes}
              onChange={handleFieldChange}
              disabled={isDetailView}
            />
          </div>

          <div className="mb-4">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">Company</p>
            <SearchableSelectInput
              name="company_id"
              value={formData.company_id}
              options={[
                { value: '', label: 'None' },
                ...(invitationData?.company_options || [])
              ]}
              onChange={(name, value) => {
                const selected = (invitationData?.company_options || []).find((o) => String(o.value) === String(value));
                setFormData((prev) => ({
                  ...prev,
                  company_id: value,
                  company_name: selected ? selected.label : '',
                }));
              }}
              disabled={isDetailView}
              placeholder="Search companies..."
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Start Date"
              name="start_date"
              value={formData.start_date}
              type="date"
              handleChange={(e) => handleFieldChange('start_date', e.target.value)}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="User Expiration Date"
              name="user_expiration_date"
              value={formData.user_expiration_date}
              type="date"
              handleChange={(e) => handleFieldChange('user_expiration_date', e.target.value)}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Alex Expiration Date"
              name="alex_expiration_date"
              value={formData.alex_expiration_date}
              type="date"
              handleChange={(e) => handleFieldChange('alex_expiration_date', e.target.value)}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">Splash Screen</p>
            <SelectInput
              name="splash_screen"
              value={formData.splash_screen}
              options={splashScreenOptions}
              onChange={handleFieldChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">Status</p>
            <SelectInput
              name="status"
              value={formData.status}
              options={statusOptions}
              onChange={handleFieldChange}
              disabled={isDetailView}
            />
          </div>

          <div className="mb-4">
            <FormInput
              label="Cohort Pricing"
              name="cohort_pricing"
              value={formData.cohort_pricing}
              type="text"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="CRA Contact"
              name="cra_contact"
              value={formData.cra_contact}
              type="text"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Account Manager"
              name="account_manager"
              value={formData.account_manager}
              type="text"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Primary Client Contact Name"
              name="primary_client_contact_name"
              value={formData.primary_client_contact_name}
              type="text"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Primary Client Contact Email"
              name="primary_client_contact_email"
              value={formData.primary_client_contact_email}
              type="text"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Total Seats"
              name="total_seats"
              value={formData.total_seats}
              type="number"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Used Seats"
              name="used_seats"
              value={formData.used_seats}
              type="number"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <FormInput
              label="Available Seats"
              name="available_seats"
              value={formData.available_seats}
              type="number"
              handleChange={handleChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">Reporting Cadence</p>
            <SelectInput
              name="reporting_cadence"
              value={formData.reporting_cadence}
              options={[
                { value: '', label: '' },
                { value: 'none', label: 'None' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' }
              ]}
              onChange={handleFieldChange}
              disabled={isDetailView}
            />
          </div>
          <div className="mb-4">
            <label className="font-semibold text-gray-700">Cohort Notes</label>
            <textarea
              name="cohort_notes"
              value={formData.cohort_notes}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              rows="4"
              disabled={isDetailView}
            />
          </div>

          <div className="pb-2">
            <p className="text-sm uppercase text-charcoal font-bold pb-2">Modules</p>
            <div className="rounded-lg border-2 border-gray p-2 relative overflow-hidden">
              <div className="p-4 flex items-center border-b-2 border-gray">
                <input
                  id="select-all"
                  className="mr-4"
                  type="checkbox"
                  checked={isCheckAll}
                  onChange={(e) => handleCheckAll(e.target.checked)}
                />
                <label htmlFor="select-all">Select All</label>
              </div>
              {courses && courses.length > 0 ? courses.map((course) => {
                const { id, title } = course;
                return (
                  <div className="px-4 py-2 flex items-center" key={id}>
                    <input
                      id={id}
                      className="mr-4"
                      type="checkbox"
                      checked={selectedCourses.includes(id)}
                      onChange={(e) =>
                        handleCourseCheck(e.target.checked, id)
                      }
                    />
                    <label htmlFor={id}>{title}</label>
                  </div>
                );
              }) : <div className="p-4 text-gray-500">No courses available</div>}
            </div>
          </div>

        </div>

        {!isDetailView && (
          <button
            className="mb-6 p-4 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            type="submit"
            disabled={!formData.name.trim()}
          >
            {type === 'create' ? 'Create Cohort' : 'Update Cohort'}
          </button>
        )}

        {(createLoading || updateLoading) && <FormLoader />}
      </form>
    </>
  );
};

export default CohortForm;
